import { Box, Typography } from "@mui/material";
import { ContentWrapper } from "@src/components";
import { FormattedDateWidthDateTime } from "@src/components/FormattedDateWithDateTime";
import { type GQLCameraAdminQuery } from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { StyledTypography } from "./SubscriptionInformation.sc";

interface InformationProps {
    basicEndDate: Date | null;
    premiumEndDate: Date | null;
}

const BasicInformation = ({ endDate }: { endDate: Date | null }) => {
    const intl = useIntl();

    if (!endDate) {
        return null;
    }

    return (
        <StyledTypography>
            <FormattedMessage id="reviermanager.camera.subscription.basicEndDate.information" defaultMessage="Das Abonnement endet am:" />{" "}
            <FormattedDateWidthDateTime
                variant="bold"
                value={endDate}
                separator={` ${intl.formatMessage({
                    id: "reviermanager.camera.subscription.basicEndDate.separator",
                    defaultMessage: "um",
                })} `}
                endAdornment={` ${intl.formatMessage({
                    id: "reviermanager.camera.subscription.basicEndDate.endAdornment",
                    defaultMessage: "Uhr",
                })} `}
            />
        </StyledTypography>
    );
};

const PremiumInformation = ({ endDate }: { endDate: Date | null }) => {
    const intl = useIntl();

    if (!endDate) {
        return null;
    }

    return (
        <>
            <Box sx={{ m: 2 }} />
            <StyledTypography>
                <FormattedMessage
                    id="reviermanager.camera.subscription.premiumEndDate.information"
                    defaultMessage="Das Premium Abonnement endet am:"
                />{" "}
                <FormattedDateWidthDateTime
                    variant="bold"
                    value={endDate}
                    separator={` ${intl.formatMessage({
                        id: "reviermanager.camera.subscription.basicEndDate.separator",
                        defaultMessage: "um",
                    })} `}
                    endAdornment={` ${intl.formatMessage({
                        id: "reviermanager.camera.subscription.basicEndDate.endAdornment",
                        defaultMessage: "Uhr",
                    })} `}
                />
            </StyledTypography>
        </>
    );
};

const Information: FC<PropsWithChildren<InformationProps>> = ({ basicEndDate, premiumEndDate }) => {
    if (!basicEndDate) {
        return (
            <Typography>
                <FormattedMessage id="reviermanager.camera.noSubscription" defaultMessage="Kein Abonnement" />
            </Typography>
        );
    }

    return (
        <>
            <BasicInformation endDate={basicEndDate} />
            <PremiumInformation endDate={premiumEndDate} />
        </>
    );
};

const SubscriptionInformation: FC<PropsWithChildren<GQLCameraAdminQuery>> = ({ cameraAdmin }) => {
    const intl = useIntl();

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.camera.subscription", defaultMessage: "Abonnement" })}>
            <Information basicEndDate={cameraAdmin.licenceInfo.basicEndDate} premiumEndDate={cameraAdmin.licenceInfo.premiumEndDate} />
        </ContentWrapper>
    );
};

export default SubscriptionInformation;
